import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiExclamationTriangle, HiXCircle } from 'react-icons/hi2';
import { StorageUsage } from '../../../types/storage';
import { formatFileSize, calculateStoragePercentage, getStorageWarningLevel } from '../../../utils/storageUtils';

interface StorageUsageBarProps {
  usage: StorageUsage | null;
}

export default function StorageUsageBar({ usage }: StorageUsageBarProps): JSX.Element {
  const { t } = useTranslation();
  
  if (!usage) {
    return (
      <div className="flex items-center gap-4 text-sm text-gray-500">
        <div className="animate-pulse">{t('page.files.storageUsageBar.loading')}</div>
      </div>
    );
  }

  const percentage = calculateStoragePercentage(usage.total, usage.maxUsage);
  const warningLevel = getStorageWarningLevel(percentage);

  const getProgressColor = (): string => {
    switch (warningLevel) {
      case 'critical':
        return 'bg-danger';
      case 'warning':
        return 'bg-secondary';
      default:
        return 'bg-primary';
    }
  };

  const getTextColor = (): string => {
    switch (warningLevel) {
      case 'critical':
        return 'text-danger';
      case 'warning':
        return 'text-secondary';
      default:
        return 'text-gray-700';
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col max-w-md">
        <div className="flex justify-between gap-4 text-sm mb-1">
          <span className={`font-medium ${getTextColor()}`}>
            {formatFileSize(usage.total)} / {formatFileSize(usage.maxUsage)}
          </span>
          <span className={`text-sm ${getTextColor()}`}>{percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>

      {warningLevel === 'warning' && (
        <div className="text-sm text-secondary flex items-center gap-1">
          <HiExclamationTriangle className="w-5 h-5" />
          <span>{t('page.files.storageUsageBar.almostFull')}</span>
        </div>
      )}

      {warningLevel === 'critical' && (
        <div className="text-sm text-danger flex items-center gap-1 font-medium">
          <HiXCircle className="w-5 h-5" />
          <span>{t('page.files.storageUsageBar.limitReached')}</span>
        </div>
      )}
    </div>
  );
}
